import { format } from 'date-fns';
import type { MunicipalListFormValues } from '../schemas/municipal-list.schema';
import type { PreRegisteredAccommodationFormValues } from '../schemas/pre-registered-accommodation.schema';
import type {
  MunicipalListAccommodation,
  MunicipalListDetail,
  MunicipalListPayload,
  PreRegisteredAccommodationPayload,
} from '../types/pre-registration.types';

// Locality select value for "Sin localidad" (Radix Select does not accept '')
export const NO_LOCALITY_VALUE = 'none';

const emptyToNull = (value: string | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};

const countToNumber = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

// `YYYY-MM-DD` → ISO of local midnight, the value DateField works with
const dateOnlyToIso = (value: string | null): string | undefined => {
  if (!value) return undefined;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toISOString();
};

export const toMunicipalListFormValues = (
  list?: MunicipalListDetail
): MunicipalListFormValues => ({
  municipalityId: list ? String(list.municipalityId) : '',
  surveyDate: dateOnlyToIso(list?.surveyDate ?? null),
  listManagerName: list?.listManagerName ?? '',
  observations: list?.observations ?? '',
});

export const toMunicipalListPayload = (values: MunicipalListFormValues): MunicipalListPayload => ({
  municipalityId: Number(values.municipalityId),
  // Local date, so the selected day never shifts with the timezone
  surveyDate: values.surveyDate ? format(new Date(values.surveyDate), 'yyyy-MM-dd') : null,
  listManagerName: emptyToNull(values.listManagerName),
  observations: emptyToNull(values.observations),
});

export const toAccommodationFormValues = (
  row?: MunicipalListAccommodation
): PreRegisteredAccommodationFormValues => ({
  establishmentName: row?.establishmentName ?? '',
  localityId: row?.localityId ? String(row.localityId) : NO_LOCALITY_VALUE,
  address: row?.address ?? '',
  establishmentPhone: row?.establishmentPhone ?? '',
  contactName: row?.contactName ?? '',
  contactPhone: row?.contactPhone ?? '',
  places: row?.places === null || row?.places === undefined ? '' : String(row.places),
  rooms: row?.rooms === null || row?.rooms === undefined ? '' : String(row.rooms),
  legalInstrument: row?.legalInstrument ?? '',
  observations: row?.observations ?? '',
});

export const toAccommodationPayload = (
  values: PreRegisteredAccommodationFormValues
): PreRegisteredAccommodationPayload => ({
  establishmentName: values.establishmentName.trim(),
  localityId: values.localityId === NO_LOCALITY_VALUE ? null : Number(values.localityId),
  address: emptyToNull(values.address),
  establishmentPhone: emptyToNull(values.establishmentPhone),
  contactName: emptyToNull(values.contactName),
  contactPhone: emptyToNull(values.contactPhone),
  places: countToNumber(values.places),
  rooms: countToNumber(values.rooms),
  legalInstrument: emptyToNull(values.legalInstrument),
  observations: emptyToNull(values.observations),
});
